import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter, Youtube } from "lucide-react";
import { publicSettingsQueryOptions } from "@/lib/settings.queries";
import type { SocialLinks } from "@/lib/settings.functions";

/** Footer social row: outline icons, rendered only for profiles with a URL. */
const SOCIAL: Array<{ key: keyof SocialLinks; label: string; icon: typeof Linkedin }> = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "x", label: "X", icon: Twitter },
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "youtube", label: "YouTube", icon: Youtube },
];

export function Footer() {
  // Placeholder data is the shipped defaults, so the footer never flashes.
  const { data: settings } = useQuery(publicSettingsQueryOptions());
  const org = settings!.org;
  const social = SOCIAL.filter((s) => settings!.seo.social[s.key].trim() !== "");
  return (
    <footer className="mt-24 border-t bg-background">
      <div className="brand-gradient h-1 w-full" />
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2">
            <img
              src="/logos/original/mii-logo-final.svg"
              alt=""
              width={40}
              height={40}
              className="h-10 w-10"
            />
            <span className="font-semibold">Medical Insight</span>
          </div>
          <p className="mt-3 text-justify hyphens-auto text-sm text-muted-foreground">
            Leading the global medical aesthetic market through strategic research, industry
            insight, and the Aesthetic Industry Association.
          </p>
          <address className="mt-4 space-y-1.5 text-sm not-italic text-muted-foreground">
            {org.addressLines.length > 0 && (
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  <span className="block">{org.companyName}</span>
                  {org.addressLines.map((line, i) => (
                    <span key={i} className="block">
                      {line}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {org.phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0" aria-hidden />
                <a
                  href={`tel:${org.phone.replace(/[^\d+]/g, "")}`}
                  className="hover:text-foreground"
                >
                  {org.phone}
                </a>
              </div>
            )}
            {org.generalEmail && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0" aria-hidden />
                <a href={`mailto:${org.generalEmail}`} className="hover:text-foreground">
                  {org.generalEmail}
                </a>
              </div>
            )}
          </address>
          {social.length > 0 && (
            <ul className="mt-4 flex items-center gap-3" aria-label="Social profiles">
              {social.map(({ key, label, icon: Icon }) => (
                <li key={key}>
                  <a
                    href={settings!.seo.social[key]}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    title={label}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        <FooterCol
          title="Medical Insight"
          items={[
            { label: "About", href: "/p/about-us" },
            { label: "Strategic Research", href: "/research" },
            { label: "Aesthetic Insights", href: "/news" },
            { label: "Gallery", href: "/gallery" },
            { label: "Contact", href: "/p/contact" },
          ]}
        />
        <FooterCol
          title="Aesthetic Industry Association"
          items={[
            { label: "Join Now", href: "/p/industry-association" },
            { label: "Advisory Board", href: "/board" },
            { label: "AIA Awards", href: "/awards" },
            { label: "Sponsors", href: "/sponsors" },
          ]}
        />
        <FooterCol
          title="Events"
          items={[
            { label: "The Beauty Show", href: "/beauty-show" },
            { label: "Ax Incubator", href: "/p/ax-incubator" },
            // Press points at Contact until a press page exists.
            { label: "Press", href: "/p/contact" },
          ]}
        />
      </div>
      <div className="border-t">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-4 text-xs text-muted-foreground sm:flex-row">
          <span>© {org.legalName || org.companyName}. All Rights Reserved.</span>
          <span className="flex gap-4">
            <a href="/p/privacy" className="hover:text-foreground">
              Privacy Statement
            </a>
            <a href="/p/terms" className="hover:text-foreground">
              Terms and Conditions
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}

/** Every footer link carries a destination; a label on its own does not compile. */
type FooterLink = { label: string; href: string };

function FooterCol({ title, items }: { title: string; items: FooterLink[] }) {
  return (
    <div>
      <h4 className="text-sm font-semibold">{title}</h4>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
        {items.map(({ label, href }) => (
          <li key={label}>
            {/* Template pages (/p/…) are not typed routes; a plain anchor keeps
                them honest. */}
            {href.startsWith("/p/") ? (
              <a href={href} className="hover:text-foreground">
                {label}
              </a>
            ) : (
              <Link to={href} className="hover:text-foreground">
                {label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
